/**
 * Double Ratchet Algorithm
 *
 * After X3DH establishes a shared secret, the Double Ratchet provides:
 * - Forward secrecy: Past messages can't be decrypted if keys are compromised
 * - Break-in recovery: Future messages are safe even after key compromise
 * - Out-of-order message handling
 *
 * Reference: https://signal.org/docs/specifications/doubleratchet/
 */

import { x25519DH, generateX25519KeyPair, toBase64 } from "./keys.js";
import type { KeyPair } from "./keys.js";
import { hkdf } from "@noble/hashes/hkdf";
import { sha256 } from "@noble/hashes/sha256";
import { hmac } from "@noble/hashes/hmac";
import { gcm } from "@noble/ciphers/aes";
import { randomBytes } from "@noble/hashes/utils";

// ============================================================
// Types
// ============================================================

export interface RatchetState {
  // DH Ratchet
  dhSendingKeyPair: KeyPair; // Our current DH ratchet key pair
  dhReceivingKey: Uint8Array | null; // Their current DH ratchet public key

  // Root chain
  rootKey: Uint8Array; // 32 bytes

  // Sending chain
  sendingChainKey: Uint8Array | null;
  sendingMessageNumber: number;

  // Receiving chain
  receivingChainKey: Uint8Array | null;
  receivingMessageNumber: number;

  // Previous sending chain length (for header)
  previousSendingChainLength: number;

  // Skipped message keys (for out-of-order messages)
  // Map<"publicKey:messageNumber", messageKey>
  skippedKeys: Map<string, Uint8Array>;
}

export interface MessageHeader {
  dhPublicKey: Uint8Array; // Sender's current DH ratchet public key
  previousChainLength: number; // Length of previous sending chain
  messageNumber: number; // Message number in current chain
}

export interface EncryptedMessage {
  header: MessageHeader;
  ciphertext: Uint8Array; // AES-256-GCM encrypted
  nonce: Uint8Array; // 12 bytes
  tag: Uint8Array; // 16 bytes (GCM auth tag, embedded in ciphertext)
}

// ============================================================
// Constants
// ============================================================

const MAX_SKIP = 1000; // Maximum number of skipped message keys to store
const RATCHET_INFO = new TextEncoder().encode("KollegePlusRatchet");

// ============================================================
// Ratchet Initialization
// ============================================================

/**
 * Initialize ratchet state for the session initiator (Alice/sender)
 * Called after X3DH, with the shared secret as root key
 */
export function initSender(
  sharedSecret: Uint8Array,
  recipientDHPublicKey: Uint8Array,
): RatchetState {
  const dhKeyPair = generateX25519KeyPair();

  // Perform initial DH ratchet step
  const dhOutput = x25519DH(dhKeyPair.privateKey, recipientDHPublicKey);
  const [rootKey, sendingChainKey] = kdfRootKey(sharedSecret, dhOutput);

  return {
    dhSendingKeyPair: dhKeyPair,
    dhReceivingKey: recipientDHPublicKey,
    rootKey,
    sendingChainKey,
    sendingMessageNumber: 0,
    receivingChainKey: null,
    receivingMessageNumber: 0,
    previousSendingChainLength: 0,
    skippedKeys: new Map(),
  };
}

/**
 * Initialize ratchet state for the session responder (Bob/recipient)
 * Called after X3DH, with the shared secret as root key
 */
export function initRecipient(
  sharedSecret: Uint8Array,
  signedPreKeyPair: KeyPair,
): RatchetState {
  return {
    dhSendingKeyPair: signedPreKeyPair,
    dhReceivingKey: null,
    rootKey: sharedSecret,
    sendingChainKey: null,
    sendingMessageNumber: 0,
    receivingChainKey: null,
    receivingMessageNumber: 0,
    previousSendingChainLength: 0,
    skippedKeys: new Map(),
  };
}

// ============================================================
// State Cloning (for atomic decrypt)
// ============================================================

/**
 * Deep-clone a RatchetState.
 *
 * Cannot use structuredClone (not available in all runtimes) or JSON (loses
 * Uint8Array identity). All Uint8Array fields are copied with .slice() so the
 * clone is fully independent of the original.
 */
function cloneRatchetState(state: RatchetState): RatchetState {
  const skippedKeys = new Map<string, Uint8Array>();
  for (const [k, v] of state.skippedKeys) {
    skippedKeys.set(k, v.slice());
  }

  return {
    dhSendingKeyPair: {
      privateKey: state.dhSendingKeyPair.privateKey.slice(),
      publicKey: state.dhSendingKeyPair.publicKey.slice(),
    },
    dhReceivingKey: state.dhReceivingKey ? state.dhReceivingKey.slice() : null,
    rootKey: state.rootKey.slice(),
    sendingChainKey: state.sendingChainKey ? state.sendingChainKey.slice() : null,
    receivingChainKey: state.receivingChainKey ? state.receivingChainKey.slice() : null,
    sendingMessageNumber: state.sendingMessageNumber,
    receivingMessageNumber: state.receivingMessageNumber,
    previousSendingChainLength: state.previousSendingChainLength,
    skippedKeys,
  };
}

/**
 * Commit all fields of a clone back to the original state object in-place.
 * Called only after a successful decrypt so that any throw leaves `state`
 * exactly as it was before the ratchetDecrypt call.
 */
function commitClone(state: RatchetState, clone: RatchetState): void {
  state.dhSendingKeyPair = clone.dhSendingKeyPair;
  state.dhReceivingKey = clone.dhReceivingKey;
  state.rootKey = clone.rootKey;
  state.sendingChainKey = clone.sendingChainKey;
  state.receivingChainKey = clone.receivingChainKey;
  state.sendingMessageNumber = clone.sendingMessageNumber;
  state.receivingMessageNumber = clone.receivingMessageNumber;
  state.previousSendingChainLength = clone.previousSendingChainLength;
  state.skippedKeys = clone.skippedKeys;
}

// ============================================================
// Encrypt / Decrypt
// ============================================================

/**
 * Encrypt a plaintext message using the Double Ratchet
 *
 * Returns the encrypted message with header (to be sent to recipient)
 */
export function ratchetEncrypt(
  state: RatchetState,
  plaintext: Uint8Array,
  associatedData: Uint8Array,
): EncryptedMessage {
  // Derive message key from sending chain
  if (!state.sendingChainKey) {
    throw new Error("Ratchet: No sending chain key — ratchet not fully initialized");
  }

  const [newChainKey, messageKey] = kdfChainKey(state.sendingChainKey);
  state.sendingChainKey = newChainKey;

  const header: MessageHeader = {
    dhPublicKey: state.dhSendingKeyPair.publicKey,
    previousChainLength: state.previousSendingChainLength,
    messageNumber: state.sendingMessageNumber,
  };

  state.sendingMessageNumber++;

  // Encrypt with AES-256-GCM
  const encrypted = aesGcmEncrypt(messageKey, plaintext, encodeHeader(header, associatedData));

  return {
    header,
    ...encrypted,
  };
}

/**
 * Decrypt a received encrypted message using the Double Ratchet.
 *
 * Atomic: if decryption fails for any reason (including GCM authentication
 * failure, MAX_SKIP exceeded, or missing chain key) the `state` object is
 * left completely unchanged.  State is advanced only after a successful
 * aesGcmDecrypt call.
 */
export function ratchetDecrypt(
  state: RatchetState,
  message: EncryptedMessage,
  associatedData: Uint8Array,
): Uint8Array {
  const aad = encodeHeader(message.header, associatedData);

  // ── Skipped-key path (out-of-order messages) ────────────────────────────
  // Decrypt BEFORE removing the key so that a GCM failure leaves skippedKeys
  // intact and the caller can retry with the correct ciphertext.
  const skippedKeyId = makeSkippedKeyId(message.header.dhPublicKey, message.header.messageNumber);
  const skippedKey = state.skippedKeys.get(skippedKeyId);

  if (skippedKey) {
    const plaintext = aesGcmDecrypt(skippedKey, message.ciphertext, message.nonce, aad);
    // Decrypt succeeded — consume the one-time skipped key
    state.skippedKeys.delete(skippedKeyId);
    return plaintext;
  }

  // ── Normal path — operate on a clone so original state is never mutated ──
  // before a successful decrypt.
  const clone = cloneRatchetState(state);

  const headerKeyChanged =
    !clone.dhReceivingKey ||
    !uint8ArrayEqual(message.header.dhPublicKey, clone.dhReceivingKey);

  if (headerKeyChanged) {
    // Skip any remaining messages from the previous chain
    if (clone.dhReceivingKey && clone.receivingChainKey) {
      skipMessageKeys(clone, message.header.previousChainLength);
    }

    // DH Ratchet step (mutates clone only)
    dhRatchetStep(clone, message.header);
  }

  // Skip any messages in the current chain
  skipMessageKeys(clone, message.header.messageNumber);

  // Derive message key
  if (!clone.receivingChainKey) {
    throw new Error("Ratchet: No receiving chain key");
  }

  const [newChainKey, messageKey] = kdfChainKey(clone.receivingChainKey);
  clone.receivingChainKey = newChainKey;
  clone.receivingMessageNumber++;

  // Throws on GCM authentication failure — clone is discarded, state untouched
  const plaintext = aesGcmDecrypt(messageKey, message.ciphertext, message.nonce, aad);

  // Decrypt succeeded — commit all state advances to the caller's state object
  commitClone(state, clone);

  return plaintext;
}

// ============================================================
// Internal: DH Ratchet Step
// ============================================================

function dhRatchetStep(state: RatchetState, header: MessageHeader): void {
  state.previousSendingChainLength = state.sendingMessageNumber;
  state.sendingMessageNumber = 0;
  state.receivingMessageNumber = 0;
  state.dhReceivingKey = header.dhPublicKey;

  // Receiving chain: DH with their new public key and our current private key
  const dhReceive = x25519DH(state.dhSendingKeyPair.privateKey, state.dhReceivingKey);
  const [rootKey1, receivingChainKey] = kdfRootKey(state.rootKey, dhReceive);
  state.rootKey = rootKey1;
  state.receivingChainKey = receivingChainKey;

  // New DH key pair for sending
  state.dhSendingKeyPair = generateX25519KeyPair();
  const dhSend = x25519DH(state.dhSendingKeyPair.privateKey, state.dhReceivingKey);
  const [rootKey2, sendingChainKey] = kdfRootKey(state.rootKey, dhSend);
  state.rootKey = rootKey2;
  state.sendingChainKey = sendingChainKey;
}

// ============================================================
// Internal: Skip Message Keys
// ============================================================

function skipMessageKeys(state: RatchetState, until: number): void {
  if (!state.receivingChainKey) return;

  if (until - state.receivingMessageNumber > MAX_SKIP) {
    throw new Error("Ratchet: Too many skipped messages — possible attack");
  }

  while (state.receivingMessageNumber < until) {
    const [newChainKey, messageKey] = kdfChainKey(state.receivingChainKey);
    state.receivingChainKey = newChainKey;

    const keyId = makeSkippedKeyId(state.dhReceivingKey!, state.receivingMessageNumber);
    state.skippedKeys.set(keyId, messageKey);
    state.receivingMessageNumber++;

    // Evict oldest skipped keys if too many
    if (state.skippedKeys.size > MAX_SKIP) {
      const firstKey = state.skippedKeys.keys().next().value;
      if (firstKey) state.skippedKeys.delete(firstKey);
    }
  }
}

// ============================================================
// KDF Functions
// ============================================================

/** KDF for root key chain: derives new root key + chain key from DH output */
function kdfRootKey(rootKey: Uint8Array, dhOutput: Uint8Array): [Uint8Array, Uint8Array] {
  const derived = hkdf(sha256, dhOutput, rootKey, RATCHET_INFO, 64);
  return [derived.slice(0, 32), derived.slice(32, 64)];
}

/** KDF for message chain: derives new chain key + message key */
function kdfChainKey(chainKey: Uint8Array): [Uint8Array, Uint8Array] {
  const newChainKey = hmac(sha256, chainKey, new Uint8Array([0x01]));
  const messageKey = hmac(sha256, chainKey, new Uint8Array([0x02]));
  return [newChainKey, messageKey];
}

// ============================================================
// AES-256-GCM Encryption (using Web Crypto API)
// ============================================================

function aesGcmEncrypt(
  key: Uint8Array,
  plaintext: Uint8Array,
  aad: Uint8Array,
): { ciphertext: Uint8Array; nonce: Uint8Array; tag: Uint8Array } {
  const nonce = randomBytes(12);
  const cipher = gcm(key, nonce, aad);
  const encrypted = cipher.encrypt(plaintext);

  // GCM appends 16-byte auth tag; return full blob + separated tag for header
  const tag = encrypted.slice(encrypted.length - 16);

  return { ciphertext: encrypted, nonce, tag };
}

function aesGcmDecrypt(
  key: Uint8Array,
  ciphertext: Uint8Array,
  nonce: Uint8Array,
  aad: Uint8Array,
): Uint8Array {
  const cipher = gcm(key, nonce, aad);
  return cipher.decrypt(ciphertext);
}

// ============================================================
// Helpers
// ============================================================

function makeSkippedKeyId(publicKey: Uint8Array, messageNumber: number): string {
  return `${toBase64(publicKey)}:${messageNumber}`;
}

function encodeHeader(header: MessageHeader, associatedData: Uint8Array): Uint8Array {
  // AD = associatedData || header (for AEAD)
  const headerBytes = new Uint8Array(32 + 4 + 4); // publicKey + prevChainLen + msgNum
  headerBytes.set(header.dhPublicKey, 0);
  new DataView(headerBytes.buffer).setUint32(32, header.previousChainLength, false);
  new DataView(headerBytes.buffer).setUint32(36, header.messageNumber, false);

  const result = new Uint8Array(associatedData.length + headerBytes.length);
  result.set(associatedData, 0);
  result.set(headerBytes, associatedData.length);
  return result;
}

function uint8ArrayEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
